import type { JobAdsRequest } from "@/modules/jobAds/client/api/types"
import type { CurrentUserCurriculumIds, SearchConfigFilter } from "@/modules/user/api/types"

import { LOCALE_NONE, MAX_JOB_ADS, MAX_SPONSORED_JOB_ADS } from "@/modules/jobAds/client/constants"
import { computeAvailableSearchConfigOptions } from "@/modules/jobAds/client/utils/computeAvailableSearchConfigOptions"
import { computeJobAdsLocationRequest } from "@/modules/jobAds/client/utils/computeJobAdsLocationRequest"

export type JobAdsRequestParameters = {
  companyIds?: string[] | null
  curriculumIds: CurrentUserCurriculumIds | undefined
  schoolId: string
  locales?: string[] | null
  page: number
  contractTypes: string[] | undefined | null
  locations?: string[] | null
  positionCategories: string[] | undefined | null
  userQuery: string
  contractTypesSearchConfig: SearchConfigFilter | undefined | null
  studyLevelsSearchConfig: SearchConfigFilter | undefined | null
  sponsoredResultsMax?: number | null
  radiusKm?: number | null
  contractDurations?: string[] | null
  studyLevels?: string[] | null
  startDates?: string[] | null
  tags?: string[] | null
  exclusive: string[] | undefined | null
  companySectors?: string[] | null
}

export const computeJobAdsRequest = ({
  contractTypesSearchConfig,
  studyLevelsSearchConfig,
  companyIds = [],
  contractDurations = [],
  contractTypes = [],
  curriculumIds,
  locales = [],
  locations,
  page,
  positionCategories = [],
  radiusKm,
  schoolId,
  startDates = [],
  studyLevels = [],
  userQuery,
  exclusive,
  tags = [],
  companySectors = [],
}: JobAdsRequestParameters): JobAdsRequest => {
  const paginationJobAdsRequest = {
    ...(page > 0 && { page_token: String(page - 1) }),
  }

  const ccFiltersRequest = {
    ...(exclusive && exclusive.includes("reserved") && { exclusive: true }),
    ...(exclusive && exclusive.includes("partner_career_center") && { partner_school_ids: [schoolId] }),
    ...(exclusive && exclusive.includes("created_by_student_or_alumni") && { from_alumni: true }),
  }

  const filteringJobAdsRequest = {
    ...(positionCategories?.length && { position_categories: positionCategories }),
    ...(contractTypes?.length && { contract_types: contractTypes }),
    ...(locations && { locations: computeJobAdsLocationRequest(locations) }),
    ...(contractDurations?.length && { contract_durations: contractDurations }),
    ...(studyLevels?.length && {
      career_center_study_levels: studyLevels.map(Number),
    }),
    ...(startDates?.length && { start_dates: startDates }),
    ...(!contractTypes?.length &&
      contractTypesSearchConfig && {
        contract_types: computeAvailableSearchConfigOptions(contractTypesSearchConfig),
      }),
    ...(!studyLevels?.length &&
      studyLevelsSearchConfig && {
        career_center_study_levels: computeAvailableSearchConfigOptions(studyLevelsSearchConfig).map(Number),
      }),
    ...(tags?.length && { tags }),
    ...(companySectors?.length && { company_sectors: companySectors }),
  }

  const jobAdsSearchIndexRequest = {
    ...(!companyIds?.length && { sponsored_results_max: MAX_SPONSORED_JOB_ADS }),
  }

  const companyJobAdsIndexRequest = {
    ...(companyIds?.length && { company_ids: companyIds }),
  }

  const cleanLocales = (locales || []).filter(locale => locale !== LOCALE_NONE)

  const jobAdsRequest: JobAdsRequest = {
    company_ids: [],
    curriculum_ids: curriculumIds || [],
    page_size: MAX_JOB_ADS,
    school_ids: [],
    ...(locales && { locales: cleanLocales }),
    ...(schoolId && { school_ids: [schoolId] }),
    ...(userQuery.length && { query: userQuery }),
    ...(radiusKm && { radius_km: radiusKm }),
    ...jobAdsSearchIndexRequest,
    ...companyJobAdsIndexRequest,
    ...paginationJobAdsRequest,
    ...ccFiltersRequest,
    ...filteringJobAdsRequest,
  }

  return jobAdsRequest
}
